import React, { useState, memo, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Autocomplete from '@material-ui/lab/Autocomplete';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import { debounce } from 'lodash';
import { setSelectedWatchlist } from '../../reducers/Watchlist';
import { getCompanyFromGlobalStatesByCompanyId, isActive } from './WatchlistHelpers';
import { useHistory } from 'react-router-dom';
import { getGlobalSearchOptions, setReacntIdAndDocumentType } from 'global/GlobalActions';
import IconButton from '@material-ui/core/IconButton';
import SearchIcon from '@material-ui/icons/Search';
import clsx from 'clsx';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Backdrop from '@material-ui/core/Backdrop';
import Popper from '@material-ui/core/Popper';
import { resetThemeXSearch } from 'components/topic/topicHelpers';
import Paper from '@material-ui/core/Paper';
import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import { useLocation } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@material-ui/core';

const GloballyCompanySearch = () => {
  const history = useHistory();
  const classes = useStyles();
  const dispatch = useDispatch();
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [availableOptions, setAvailableOptions] = useState([]);
  const [clickedOption, setClickedOption] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(0);
  const [showGlobalSearchBar, setShowGlobalSearchBar] = useState(true);
  const inputRef = useRef(null);

  const handleSearchTextChange = debounce(async searchText => {
    if (!searchText) return;
    setAvailableOptions([]);
    setLoadingOptions(prevState => prevState + 1);
    const responseOptions = await getGlobalSearchOptions(searchText);

    setLoadingOptions(prevState => prevState - 1);
    setAvailableOptions(responseOptions ?? []);
  }, 500);

  const selectionChanged = (e, selectedOption) => {
    setClickedOption(selectedOption);
    if (selectedOption?.companyId) {
      let company = dispatch(getCompanyFromGlobalStatesByCompanyId(selectedOption.companyId));
      if (company) {
        setReacntIdAndDocumentType(company);
      } else {
        company = selectedOption;
      }
      dispatch(setSelectedWatchlist(company));
      setTimeout(() => {
        setClickedOption(null);
        const pathname = location.pathname;
        if (isActive(pathname, company)) {
          history.push(pathname);
        } else {
          history.push('/filings');
        }
      }, [100]);
    }
  };

  const handleThemeXSearch = () => {
    const inputValue = inputRef.current?.value;
    if (!inputValue) return;
    resetThemeXSearch();

    setTimeout(() => {
      // current time stamps will help to re-render useEffect if user make same search again
      history.push(`/topic?searchText=${inputValue}&ts=${Date.now()}`);
    }, 100);
  };

  useEffect(() => {
    setShowGlobalSearchBar(!isSmallScreen);
  }, [isSmallScreen]);

  const CustomPaper = ({ className, children }) => {
    return (
      <Paper className={className}>
        {children}

        <Divider />
        <List dense disablePadding>
          <ListItem button onMouseDown={handleThemeXSearch} className={classes.themeXOption}>
            <ListItemText>Search in ThemeX</ListItemText>
          </ListItem>
        </List>
      </Paper>
    );
  };

  return (
    <>
      {showGlobalSearchBar ? (
        <div className={classes.globalSearchWidth}>
          <FormControl className={classes.formControl}>
            <Autocomplete
              loading={loadingOptions > 0}
              loadingText={'Loading...'}
              className={classes.dropdownSearchField}
              onChange={selectionChanged}
              options={availableOptions}
              value={clickedOption}
              autoHighlight // first option will be select on enter
              getOptionLabel={option => option.globalDropdownLabel}
              filterOptions={option => option}
              closeIcon={false}
              onClose={() => isSmallScreen && setShowGlobalSearchBar(false)}
              PaperComponent={CustomPaper}
              PopperComponent={props => {
                const { anchorEl, style, className, children, ...propsForBackdrop } = props;
                return (
                  <>
                    <Popper {...props} />
                    <Backdrop className={clsx(classes.backdrop, className)} {...propsForBackdrop} />
                  </>
                );
              }}
              renderInput={params => (
                <TextField
                  autoFocus={isSmallScreen}
                  {...params}
                  inputRef={inputRef}
                  variant="outlined"
                  placeholder="Company or ThemeX Search"
                  onChange={e => handleSearchTextChange(e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: 'off',
                  }}
                />
              )}
            />
          </FormControl>
        </div>
      ) : (
        <IconButton className={classes.iconBtn} color="inherit" onClick={() => setShowGlobalSearchBar(true)}>
          <SearchIcon />
        </IconButton>
      )}
    </>
  );
};

export default memo(GloballyCompanySearch);

const useStyles = makeStyles(theme => ({
  formControl: {
    width: '100%',
  },
  globalSearchWidth: {
    justifyContent: 'space-between',
    flexDirection: 'row',

    width: 200,
    [theme.breakpoints.up('lg')]: {
      width: '260px',
    },
    [theme.breakpoints.down('xs')]: {
      width: '175px',
    },
  },
  iconBtn: {
    padding: 4,
  },
  dropdownSearchField: {
    background: 'white',
    borderRadius: '12px',
    zIndex: 5,
    '& .MuiInputBase-root': {
      paddingRight: '30px !important',
    },
  },
  backdrop: {
    backgroundColor: 'transparent',
    width: '100%',
    zIndex: 2,
  },
  themeXOption: {
    textAlign: 'right',
    color: '#5383ff',
  },
}));
